import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  BodyText,
  CloudinaryImage,
  ContentfulImage,
  Icon,
  LoadingDots,
  theme,
  useResponsive,
} from '@sg/garnish';
import { AllergenProperty } from '@sg/graphql-schema';
import { useDietaryRestrictions, type AllergenTypes } from '@sg/shared-fe-features-menu';

import { HomeProductNutritionData } from '../HomeProductNutritionData';

// ────────────────────────────────────────────────────────────────────────────

export const HomeProductCard = (props: HomeProductCardProps) => {
  const {
    categoryName,
    id,
    slug,
    isModifiable,
    title = '',
    description,
    cost,
    cloudinaryImage,
    contentfulImage,
    imageA11y,
    dietaryPreferences,
    allergenProperties,
    openMenu,
    openProductDetails,
    addProductToBag,
    trackItemSelection,
  } = props;

  const { formatNumber } = useIntl();

  const { match } = useResponsive();
  const cloudinaryImageConfig = match([CLOUDINARY_IMAGE_CONFIG_XS, CLOUDINARY_IMAGE_CONFIG_SM]);
  const contentfulImageConfig = match([CONTENTFUL_IMAGE_CONFIG_XS, CONTENTFUL_IMAGE_CONFIG_SM]);

  const getRestrictionNotice = useDietaryRestrictions({ restrictions: dietaryPreferences });
  const restrictionNotice = getRestrictionNotice({ allergenProperties });

  const [isAddingToBag, setIsAddingToBag] = useState(false);

  const handlePress = useCallback(async () => {
    trackItemSelection(title, categoryName);

    if (!id) {
      openMenu();
      return;
    }

    if (isModifiable) {
      openProductDetails(slug);
      return;
    }

    setIsAddingToBag(true);
    await addProductToBag(id, title);
    setIsAddingToBag(false);
  }, [
    categoryName,
    id,
    slug,
    title,
    isModifiable,
    openProductDetails,
    addProductToBag,
    openMenu,
    trackItemSelection,
  ]);

  return (
    <Pressable style={match([styles.containerXS, styles.containerSM])} onPress={handlePress}>
      <View style={styles.imageContainer}>
        {cloudinaryImage ? (
          <CloudinaryImage
            contentFit="contain"
            baseUrl={cloudinaryImage}
            config={cloudinaryImageConfig}
            aria-label={imageA11y ?? title}
            style={match([styles.imageXS, styles.imageSM])}
          />
        ) : null}

        {contentfulImage ? (
          <ContentfulImage
            contentFit="contain"
            baseUrl={contentfulImage}
            options={contentfulImageConfig}
            aria-label={imageA11y ?? title}
            style={match([styles.imageXS, styles.imageSM])}
          />
        ) : null}

        {isModifiable || !cost ? null : (
          <View style={styles.addToBagIcon} pointerEvents="none">
            {isAddingToBag ? (
              <LoadingDots color={theme.colors.DARK_KALE} />
            ) : (
              <Icon
                name="IconPlus"
                width={ADD_ICON_SIZE}
                height={ADD_ICON_SIZE}
                color={theme.colors.DARK_KALE}
              />
            )}
          </View>
        )}
      </View>

      {title ? (
        <BodyText style={styles.title} sizeMatch={['18']} bold>
          {title}
        </BodyText>
      ) : null}

      {description ? (
        <BodyText
          sizeMatch={['14']}
          style={match([styles.descriptionXS, styles.descriptionSM])}
          numberOfLines={match([2, 3])}
        >
          {description}
        </BodyText>
      ) : null}

      {restrictionNotice ? (
        <BodyText sizeMatch={['14']} style={styles.restrictionNoticeText}>
          {restrictionNotice}
        </BodyText>
      ) : null}

      {hasNutritionData(props) ? <HomeProductNutritionData {...props} /> : null}

      {cost ? (
        <BodyText
          sizeMatch={['12']}
          style={match([styles.costXS, styles.costSM])}
          bold={match([false, true])}
        >
          {formatNumber(cost / 100, { style: 'currency', currency: 'USD' })}
        </BodyText>
      ) : null}
    </Pressable>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hasNutritionData = (props: HomeProductCardProps) => {
  return (
    props.calories ||
    props.totalFatG ||
    props.totalCarbsG ||
    props.dietaryFiberG ||
    props.sugarsG ||
    props.proteinG ||
    props.co2eKg
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE_XS = 160;
const IMAGE_SIZE_SM = 365;
const ADD_ICON_SIZE = 24;
const ADD_BUTTON_SIZE = 32;

const SHARED_CLOUDINARY_OPTIONS = {
  crop: 'crop' as const,
  height: 0.7,
  width: 0.4,
};

const SHARED_CONTENTFUL_OPTIONS = {
  fm: 'webp' as const,
  fit: 'fill' as const,
  q: 70,
};

const CLOUDINARY_IMAGE_CONFIG_XS = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_XS }];

const CLOUDINARY_IMAGE_CONFIG_SM = [SHARED_CLOUDINARY_OPTIONS, { width: IMAGE_SIZE_SM }];

const CONTENTFUL_IMAGE_CONFIG_XS = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_SIZE_XS,
};

const CONTENTFUL_IMAGE_CONFIG_SM = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_SIZE_SM,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_XS,
    maxWidth: IMAGE_SIZE_XS,
  },
  containerSM: {
    flex: 1,
    gap: theme.spacing['2'],
    minWidth: IMAGE_SIZE_SM,
    maxWidth: IMAGE_SIZE_SM,
  },
  imageContainer: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  imageXS: {
    width: IMAGE_SIZE_XS,
    height: IMAGE_SIZE_XS,
  },
  imageSM: {
    width: IMAGE_SIZE_SM,
    height: IMAGE_SIZE_SM,
  },
  title: {
    textTransform: 'uppercase',
  },
  descriptionXS: {
    color: theme.colors.CHARCOAL,
  },
  descriptionSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  costXS: {
    color: theme.colors.CHARCOAL,
  },
  costSM: {
    color: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
  addToBagIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: ADD_BUTTON_SIZE,
    height: ADD_BUTTON_SIZE,
    borderRadius: ADD_BUTTON_SIZE,
    backgroundColor: theme.colors.WHITE,
    position: 'absolute',
    bottom: theme.spacing['2'],
    right: theme.spacing['2'],
  },
  restrictionNoticeText: {
    color: theme.colors.CAUTION,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProductCardProps = {
  categoryName: string;
  id: string;
  slug: string;
  isModifiable: boolean;
  title?: string;
  description?: string;
  cost?: number;
  cloudinaryImage?: string;
  contentfulImage?: string;
  imageA11y?: string;
  dietaryPreferences: AllergenTypes;
  allergenProperties: readonly AllergenProperty[];
  calories?: number;
  totalFatG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  sugarsG?: number;
  proteinG?: number;
  co2eKg?: number;
  openMenu: () => void;
  openProductDetails: (slug: string) => void;
  addProductToBag: (id: string, title: string) => Promise<void>;
  trackItemSelection: (productId: string, categoryName: string) => void;
};
